import { createReference, formatHumanName, getExtension, MedplumClient } from '@medplum/core';
import { Organization, OrganizationContact, Patient, Reference } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import { RESOURCE_CREATION_DATE_EXTENSION } from './extensions';
import { normalizePhoneNumber } from './phone-validations';

export interface PatientPhoneMatch {
  type: 'patient';
  primary: Patient;
  allMatches: Reference<Patient>[];
}

export interface PartnerPhoneMatch {
  type: 'partner';
  primary: Organization;
  allMatches: Reference<Organization>[];
  matchedContact?: OrganizationContact;
}

export type PhoneMatch = PatientPhoneMatch | PartnerPhoneMatch | undefined;

export async function getCallInfoByPhone(
  medplum: MedplumClient,
  phoneNumber?: string,
  recipient?: string
): Promise<CallInfo> {
  if (recipient?.startsWith('Organization/')) {
    const organization = await medplum.readReference<Organization>({ reference: recipient }).catch(() => undefined);

    return {
      callerName: undefined,
      callerOrganization: organization?.name ?? 'Unknown Organization',
    };
  }

  // Fallback to phone number lookup for Patients or when no explicit recipient
  const phoneMatches = await getResourceMatchesByPhone(medplum, phoneNumber);

  if (phoneMatches?.type === 'patient') {
    const patient = phoneMatches.primary as Patient;
    return {
      callerName: formatHumanName(patient?.name?.[0]),
      callerOrganization: undefined,
    };
  }

  if (phoneMatches?.type === 'partner') {
    const { primary, matchedContact } = phoneMatches as PartnerPhoneMatch;
    const organizationName = primary.name ?? 'Unknown Organization';
    const organizationContactName = formatHumanName(matchedContact?.name);
    return {
      callerName: organizationContactName,
      callerOrganization: organizationContactName ? `(${organizationName})` : organizationName,
    };
  }

  return {
    callerName: 'Unknown',
    callerOrganization: undefined,
  };
}

// We can have many matches for the same resource, as we allow multiple patients with the same phone number
export async function getResourceMatchesByPhone(medplum: MedplumClient, phoneNumber?: string): Promise<PhoneMatch> {
  if (!phoneNumber) {
    return undefined;
  }

  const patients = await getPatientsByPhone(medplum, phoneNumber);
  if (patients.length > 0) {
    return {
      primary: patients[0],
      allMatches: patients.map(createReference),
      type: 'patient',
    } as PatientPhoneMatch;
  }

  const { partner, contact } = await getPartnerContactByPhone(medplum, phoneNumber);

  if (partner) {
    return {
      primary: partner,
      allMatches: [createReference(partner)],
      matchedContact: contact ?? undefined,
      type: 'partner',
    } as PartnerPhoneMatch;
  }

  return undefined;
}

export interface CallInfo {
  callerName?: string;
  callerOrganization?: string;
}

export async function getPartnerContactByPhone(
  medplum: MedplumClient,
  phoneNumber: string
): Promise<{ partner?: Organization; contact?: OrganizationContact }> {
  const partners = await medplum.searchResources('Organization');

  for (const partner of partners) {
    // Check Organization.telecom
    if (hasTelecomPhone(partner, phoneNumber)) {
      return { partner };
    }

    // Check Organization.contact[x].telecom
    const contact = partner.contact?.find((contact) => hasTelecomPhone(contact, phoneNumber));

    if (contact) {
      return { partner, contact };
    }
  }

  return { partner: undefined, contact: undefined };
}

function hasTelecomPhone(item: Organization | OrganizationContact, phoneNumber: string) {
  return item.telecom?.some(
    (telecom) =>
      telecom?.system?.toLowerCase() === 'phone' &&
      normalizePhoneNumber(telecom.value?.trim() ?? '') === normalizePhoneNumber(phoneNumber.trim())
  );
}

/**
 * Gets all patients for a phone number, sorted by creation date (oldest first by default).
 *
 * Handles cases where multiple patients share the same phone number (family members).
 * The first patient in the returned array is typically used as the "primary" patient
 * for communication routing and system phone number assignment.
 *
 * @param medplum - Medplum client
 * @param phoneNumber - Phone number to search (will be normalized)
 * @returns Array of patients sorted by creation date
 *
 * Note: Patients without creation date extensions are always placed last.
 */
// Medplum's phone search param is a token exact-match against the stored
// telecom.value. Historic records may be stored in any of E.164, bare
// 10-digit, or the raw form the user typed. Search for all plausible forms
// so we find the patient regardless of how their phone was saved.
export function getPhoneSearchCandidates(phoneNumber: string): string[] {
  const normalized = normalizePhoneNumber(phoneNumber);
  const candidates = new Set<string>([normalized, phoneNumber]);
  if (normalized.startsWith('+1') && normalized.length === 12) {
    candidates.add(normalized.slice(2));
  }
  return Array.from(candidates);
}

/**
 * Page size of the phone search, and so the most patients one number resolves
 * to. Family members sharing a number are a handful; a number shared by more
 * than this is placeholder data, and every caller that creates one resource
 * per match (call and SMS registers) must stay bounded. Medplum's default,
 * made explicit.
 */
export const PATIENTS_PER_PHONE_LIMIT = 20;

export async function getPatientsByPhone(medplum: MedplumClient, phoneNumber: string | undefined): Promise<Patient[]> {
  if (!phoneNumber) return [];

  const patients = await medplum.searchResources('Patient', {
    phone: getPhoneSearchCandidates(phoneNumber).join(','),
    _count: PATIENTS_PER_PHONE_LIMIT,
  });

  getExtension(patients, RESOURCE_CREATION_DATE_EXTENSION);

  return patients.sort((a, b) => {
    const aDateStr = a.extension?.find((e) => e.url === RESOURCE_CREATION_DATE_EXTENSION)?.valueDateTime;
    const bDateStr = b.extension?.find((e) => e.url === RESOURCE_CREATION_DATE_EXTENSION)?.valueDateTime;

    const aMissing = !aDateStr;
    const bMissing = !bDateStr;

    // Handle missing created at: always push them to the end and compare by id
    if (aMissing && bMissing) {
      // both missing → fallback to id
      return a.id?.localeCompare(b.id ?? '') ?? 0;
    }

    if (aMissing) return 1;
    if (bMissing) return -1;

    // Both have dates → compare them
    const aDate = dayjs(aDateStr);
    const bDate = dayjs(bDateStr);

    if (aDate.isSame(bDate)) {
      // Tie-breaker by id
      return a.id?.localeCompare(b.id ?? '') ?? 0;
    }

    return aDate.isBefore(bDate) ? -1 : 1;
  });
}
